import { formatDate } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  HANDOVER_INITIATE_VALIDATION_TYPE_HO1,
  HANDOVER_INITIATE_VALIDATION_TYPE_HO2,
  HANDOVER_INITIATE_VALIDATION_TYPE_HO2_LOC,
  HANDOVER_INITIATE_VALIDATION_TYPE_HO1_LOC,
  HANDOVER_INITIATE_VALIDATION_TYPE_HO4_PULLING,
  HANDOVER_INITIATE_VALIDATION_TYPE_HO4_WORKOVER,
  HANDOVER_INITIATE_VALIDATION_TYPE_HO5,
  HANDOVER_INITIATE_VALIDATION_TYPE_HOG,
  HANDOVER_STATUSES_EDITABLES,
  HANDOVER_STATUSES_EDITABLES_HO3,
  HANDOVER_TYPE_HO1_ID,
  HANDOVER_TYPE_HO1_LOC_ID,
  HANDOVER_TYPE_HO2_ID,
  HANDOVER_TYPE_HO2_LOC_ID,
  HANDOVER_TYPE_HO3_ID,
  HANDOVER_TYPE_HO4_ID,
  HANDOVER_TYPE_HO5_ID,
  HANDOVER_TYPE_HOG_ID,
  OPERATIONAL_AREA_COMPLETION_ID,
  OPERATIONAL_AREA_PULLING_ID,
  HANDOVER_TYPE_HOG_LOC_ID,
  HANDOVER_TYPE_HO4_LOC_ID,
  HANDOVER_INITIATE_VALIDATION_TYPE_HO4_WORKOVER_LOC,
  HANDOVER_TYPE_HO5_LOC_ID,
  HANDOVER_INITIATE_VALIDATION_TYPE_HO5_LOC,
} from '../handover-constants';
import { Abandon, AdditionalPlug } from '../interfaces/abandon';
import {
  AnnulusFluidBase,
  AnnulusInjector,
  AnnulusInjectorPressure,
  AnnulusSpace,
} from '../interfaces/annulus-space';
import { CasingLiner, BreakCasing, Fishing } from '../interfaces/casing-liner';
import {
  Cementing,
  CementingAttachmentType,
  CementingFileAttachment,
  CementingIcp,
  Plug,
  Squeeze,
} from '../interfaces/cementing';
import { DeliveryLocationWell } from '../interfaces/delivery-location-well';
import { DeliveryLocation } from '../interfaces/delivery-locations';
import { Directional } from '../interfaces/directional';
import { WellEvent } from '../interfaces/event';
import { FileAttachment } from '../interfaces/file';

import {
  Handover,
  HandoverOperationResult,
  HandoverOwValidation,
  HandoverReleaseResult,
  HandoversHistory,
  HandoversTotalCount,
  HandoverValidReceiversCount,
  ResultHandovers,
  ResultHandoversHome,
} from '../interfaces/handover';
import {
  HandoverEmitterObservation,
  HandoverHistoryObservation,
  HandoverObservation,
  HandoverReceiverObservation,
  ObservationAttachment,
} from '../interfaces/handover-observation';
import { HandoverType } from '../interfaces/handover-type';
import { HandoverTypeAreaReason } from '../interfaces/handover-type-area-reason';
import { Image } from '../interfaces/image';
import { Mandril } from '../interfaces/mandril';
import {
  Notification,
  NotificationAttachment,
  NotificationValue,
  ProductionType,
  WellType,
} from '../interfaces/notifications';
import { Perforation, PerforationIcp } from '../interfaces/perforation';
import {
  ProductionColumn,
  ProductionColumnAttachment,
  ProductionColumnHeaderRequest,
  ProductionColumnHeaderResponse,
} from '../interfaces/production-column';
import { Profile } from '../interfaces/profile';
import {
  Section,
  SectionComment,
  SectionsComments,
} from '../interfaces/section-comment';
import { SectionsCompletion } from '../interfaces/section-completion';
import { Well, WellStatusProduction } from '../interfaces/well';
import {
  BarrierElement,
  BarrierElementProblem,
  BarrierElementRelationProblem,
  RisksAlertsModel,
  Tubing,
  TubingWearReport,
} from '../interfaces/well-integrity';
import {
  Supplier,
  WellTree,
  WellTreeAttachment,
  WellTreeComponent,
  WellTreeComponentsVisibility,
} from '../interfaces/well-tree';
import {
  Wellhead,
  WellheadAttachment,
  WellheadBottomConnectorType,
  WellheadHydraulic,
  WellheadScheme,
  WellheadSectionName,
  WellheadTopConnectorType,
} from '../interfaces/wellhead';
import { BaseService } from './base.service.class';
import { NotificationService } from './notification.service';
import {
  ExecutiveSummary,
  ExecutiveSummaryAttachment,
} from '../interfaces/executive-summary';
import {
  HandoverRelation,
  RelatedHandover,
} from '../interfaces/handover-relation';
import {
  AcidificationStimulation,
  Fracture,
  FractureStimulation,
} from '../interfaces/fracture';
import { environment } from 'src/environments/environment';
import { WellTests } from '../interfaces/well-tests';

@Injectable({
  providedIn: 'root',
})
export class HandoverService extends BaseService {
  private dhoApiUrl = `${environment.urlBackend}/dhoapi`;
  private handoverTypesUrl = `${this.dhoApiUrl}/handover-types`;
  private handoversUrl = `${this.dhoApiUrl}/handovers`;
  private handoversDeliverywellUrl = `${this.dhoApiUrl}`;
  private handoversHomeUrl = `${this.dhoApiUrl}/handovers-home`;
  private handoverTypesAreaUrl = `${this.dhoApiUrl}/handover-types-area`;

  constructor(
    private http: HttpClient,
    @Inject(LOCALE_ID) private locale: string,
    protected notificationService: NotificationService
  ) {
    super(notificationService);
  }

  getHandovers(limit = 10, offset = 0): Observable<ResultHandovers> {
    const url = `${this.handoversUrl}/?limit=${limit}&offset=${offset}`;
    return this.http.get<ResultHandovers>(url).pipe(
      tap((_) => console.log('fetched handovers')),
      catchError(
        this.handleError<ResultHandovers>('getHandovers', {
          handovers: [],
          totalHandovers: 0,
        })
      )
    );
  }

  getHandoverTypes(): Observable<HandoverType[]> {
    return this.http.get<HandoverType[]>(this.handoverTypesUrl).pipe(
      tap((_) => console.log('fetched handover types')),
      catchError(this.handleError<HandoverType[]>('getHandoverTypes', []))
    );
  }

  addHandover(partialHandover: Handover): Observable<Handover> {
    return this.http
      .post<Handover>(this.handoversUrl, partialHandover)
      .pipe(
        tap((newHandover: Handover) =>
          console.log(`added handover id=${newHandover.handoverId}`)
        )
      );
  }

  getHandover(handoverId: number): Observable<Handover> {
    const url = `${this.handoversUrl}/${handoverId}`;
    return this.http.get<Handover>(url).pipe(
      tap((handover: Handover) =>
        console.log(`fetched handover id=${handover.handoverId}`)
      ),
      catchError(this.handleError<Handover>(`getHandover id=${handoverId}`))
    );
  }

  getHandoverDetailGeneral(
    handoverId: number,
    wellUwi: string
  ): Observable<Well> {
    const url = `${this.handoversUrl}/${handoverId}/general`;
    return this.http.get<Well>(url, { params: { wellUwi } }).pipe(
      tap((well: Well) =>
        console.log(`fetched handover well data uwi=${well.uwi}`)
      ),
      catchError(
        this.handleError<Well>(`getHandoverDetailGeneral HO id=${handoverId}`)
      )
    );
  }

  sendHandover(
    handoverId: number,
    handoverTypeId: number,
    handoverStatusId: number,
    emitterObservations: HandoverEmitterObservation[]
  ): Observable<HandoverOperationResult> {
    const observations = emitterObservations.map((o) => {
      return {
        handoverObservationId: o.handoverObservationId,
        handoverObservationTypeId: o.handoverObservationTypeId,
        emitterUserId: o.emitterUserId,
        emitterObservation: o.emitterObservationSolved
          ? o.emitterDescriptionObservationSolved
          : o.emitterDescriptionObservationNotSolved,
        emitterObservationDatetime: o.emitterObservationDatetime,
        emitterObservationSolved: o.emitterObservationSolved ? true : false,
        departmentObservation: o.departmentObservation,
      };
    });
    const url = `${this.handoversUrl}/${handoverId}/send?handoverTypeId=${handoverTypeId}&handoverStatusId=${handoverStatusId}`;
    return this.http.post<HandoverOperationResult>(url, observations).pipe(
      tap((_) => console.log('handover sent')),
      catchError(this.handleError<HandoverOperationResult>('sendHandover'))
    );
  }

  claimHandover(
    handoverId: number,
    handoverTypeId: number,
    isEngineeringUser: boolean
  ): Observable<Handover> {
    const url = `${this.handoversUrl}/${handoverId}/claim`;
    const body = {
      handoverTypeId: handoverTypeId,
      isEngineeringUser: isEngineeringUser,
    };
    return this.http.put<Handover>(url, body).pipe(
      tap((_) => console.log('handover claimed')),
      catchError(this.handleError<Handover>('claimHandover'))
    );
  }

  acceptHandover(
    handoverId: number,
    wellId: string,
    wellUwi: string,
    wellName: string,
    handoverTypeId: number | undefined,
    observations: HandoverReceiverObservation[]
  ): Observable<HandoverOperationResult> {
    const url = `${this.handoversUrl}/${handoverId}/accept?wellId=${wellId}&wellUwi=${wellUwi}&wellName=${wellName}&handoverTypeId=${handoverTypeId}`;
    return this.http.post<HandoverOperationResult>(url, observations).pipe(
      tap((_) => console.log('handover accepted')),
      catchError(this.handleError<HandoverOperationResult>('acceptHandover'))
    );
  }

  rejectHandover(
    handoverId: number,
    observations: HandoverReceiverObservation[],
    handoverTypeId: number
  ): Observable<HandoverOperationResult> {
    const url = `${this.handoversUrl}/${handoverId}/reject?handoverTypeId=${handoverTypeId}`;
    return this.http.post<HandoverOperationResult>(url, observations).pipe(
      tap((_) => console.log('handover rejected')),
      catchError(this.handleError<HandoverOperationResult>('rejectHandover'))
    );
  }

  releaseHandover(
    handoverId: number,
    handoverTypeId: number,
    isEngineeringUser: boolean
  ): Observable<HandoverReleaseResult> {
    const url = `${this.handoversUrl}/${handoverId}/release`;
    const body = {
      handoverTypeId: handoverTypeId,
      isEngineeringUser: isEngineeringUser,
    };
    return this.http.put<HandoverReleaseResult>(url, body).pipe(
      tap((_) => console.log('handover released')),
      catchError(this.handleError<HandoverReleaseResult>('releaseHandover'))
    );
  }

  cancelHandover(handoverId: number): Observable<Handover> {
    const url = `${this.handoversUrl}/${handoverId}/cancel`;
    return this.http.post<Handover>(url, {}).pipe(
      tap((_) => console.log('handover canceled')),
      catchError(this.handleError<Handover>('cancelHandover'))
    );
  }

  getLastObservations(
    handoverId: number,
    departmentObservation: string,
    isDr: boolean
  ): Observable<HandoverObservation[]> {
    const url = `${this.handoversUrl}/${handoverId}/observations/last?departmentObservation=${departmentObservation}&isDr=${isDr}`;
    return this.http.get<HandoverObservation[]>(url).pipe(
      tap((_) => console.log('fetched handover Last Observations')),
      catchError(
        this.handleError<HandoverObservation[]>('getLastObservations', [])
      )
    );
  }

  getObservations(
    handoverId: number
  ): Observable<HandoverHistoryObservation[]> {
    const url = `${this.handoversUrl}/${handoverId}/observations`;
    return this.http.get<HandoverHistoryObservation[]>(url).pipe(
      tap((_) => console.log('fetched handover Observations')),
      catchError(
        this.handleError<HandoverHistoryObservation[]>('getObservations', [])
      )
    );
  }

  /* Well Architecture */

  getCasingLiner(
    handoverId: number | undefined,
    wellId: string,
    handoverStatusId: number,
    handoverTypeId: number | undefined
  ): Observable<CasingLiner[]> {
    const url = `${this.handoversUrl}/${handoverId}/casings-liners?wellId=${wellId}&handoverStatusId=${handoverStatusId}&handoverTypeId=${handoverTypeId}`;
    return this.http.get<CasingLiner[]>(url).pipe(
      tap((cl: CasingLiner[]) =>
        console.log(
          `HandoverId ${handoverId} - Casing/Liner ${cl.length} fetched`
        )
      ),
      catchError(
        this.handleError<CasingLiner[]>(`getCasingLiner HO id=${handoverId}`)
      )
    );
  }

  getDirectional(
    handoverId: number | undefined,
    wellId: string,
    handoverStatusId: number,
    handoverTypeId: number | undefined
  ): Observable<Directional[]> {
    const url = `${this.handoversUrl}/${handoverId}/directionals?wellId=${wellId}&handoverStatusId=${handoverStatusId}&handoverTypeId=${handoverTypeId}`;
    return this.http.get<Directional[]>(url).pipe(
      tap((d: Directional[]) =>
        console.log(
          `HandoverId ${handoverId} - Directional ${d.length} fetched`
        )
      ),
      catchError(
        this.handleError<Directional[]>(`getDirectional HO id=${handoverId}`)
      )
    );
  }

  saveCasingLinerComment(
    casingLiner: CasingLiner[]
  ): Observable<CasingLiner[]> {
    const url = `${this.handoversUrl}/${casingLiner[0].handoverId}/casings-liners`;
    return this.http.post<CasingLiner[]>(url, casingLiner).pipe(
      tap((casingLiner: CasingLiner[]) =>
        console.log(`Comment saved! ${casingLiner}`)
      ),
      catchError(this.handleError<CasingLiner[]>(`saveCasingLiner`))
    );
  }

  getHandoverTypesArea(): Observable<HandoverTypeAreaReason[]> {
    return this.http
      .get<HandoverTypeAreaReason[]>(this.handoverTypesAreaUrl)
      .pipe(
        tap((_) => console.log('fetched handover types area')),
        catchError(
          this.handleError<HandoverTypeAreaReason[]>('getHandoverTypesArea', [])
        )
      );
  }
  /* Well Profiles */

  getProfiles(
    handoverId: number | undefined,
    wellId: string,
    handoverStatusId: number,
    handoverTypeId: number | undefined
  ): Observable<Profile[]> {
    const url = `${this.handoversUrl}/${handoverId}/profiles?wellId=${wellId}&handoverStatusId=${handoverStatusId}&handoverTypeId=${handoverTypeId}`;
    return this.http.get<Profile[]>(url).pipe(
      tap((d: Profile[]) =>
        console.log(`HandoverId ${handoverId} - Profiles: ${d.length} fetched`)
      ),
      catchError(this.handleError<Profile[]>(`getProfiles HO id=${handoverId}`))
    );
  }

  saveProfiles(profiles: Profile[]): Observable<Profile[]> {
    const url = `${this.handoversUrl}/${profiles[0].handoverId}/profiles`;
    const profilesToPost = profiles.map((value: Profile) => {
      return {
        handoverId: value.handoverId,
        wellId: value.wellId,
        wellboreId: value.wellboreId,
        logId: value.logId,
        logIntervalId: value.logIntervalId,
        holeSize: value.holeSize,
        comments: value.comments,
      };
    });
    return this.http.post<Profile[]>(url, profilesToPost).pipe(
      tap((profile: Profile[]) => console.log(`Profiles saved! ${profile}`)),
      catchError(this.handleError<Profile[]>(`saveProfiles`))
    );
  }

  /* Cemmenting */

  getCementings(
    handoverId: number | undefined,
    wellId: string,
    handoverStatusId: number,
    handoverTypeId: number | undefined
  ): Observable<Cementing[]> {
    const url = `${this.handoversUrl}/${handoverId}/cementing?wellId=${wellId}&handoverStatusId=${handoverStatusId}&handoverTypeId=${handoverTypeId}`;
    return this.http.get<Cementing[]>(url).pipe(
      tap((c: Cementing[]) => {
        console.log(
          `HandoverId ${handoverId} - Cementings: ${c.length} fetched`
        ),
          catchError(
            this.handleError<Cementing[]>(
              `getCementings HandoverId=${handoverId}`
            )
          );
      })
    );
  }

  saveCementings(cementings: Cementing[]): Observable<Cementing[]> {
    const url = `${this.handoversUrl}/${cementings[0].handoverId}/cementing`;
    const cementingsToPost = cementings.map((cementing: Cementing) => {
      return {
        handoverId: cementing.handoverId,
        wellId: cementing.wellId,
        wellboreId: cementing.wellboreId,
        cementJobId: cementing.cementJobId,
        cementStageId: cementing.cementStageId,
        makeTopJob: cementing.makeTopJob,
        comments: cementing.comments ?? '',
      };
    });
    return this.http.post<Cementing[]>(url, cementingsToPost).pipe(
      tap((cementing: Cementing[]) => {
        console.log(`Cementings saved! ${cementing}`);
      }),
      catchError(this.handleError<Cementing[]>('saveCementings'))
    );
  }

  getCementingsSqueezes(
    handoverId: number | undefined,
    wellId: string,
    handoverStatusId: number,
    handoverTypeId: number | undefined
  ): Observable<Squeeze[]> {
    const url = `${this.handoversUrl}/${handoverId}/cementing/squeeze?wellId=${wellId}&handoverStatusId=${handoverStatusId}&handoverTypeId=${handoverTypeId}`;
    return this.http.get<Squeeze[]>(url).pipe(
      tap((sq: Squeeze[]) => {
        console.log(
          `HandoverId ${handoverId} - Cementings Squeezes: ${sq.length} fetched`
        ),
          catchError(
            this.handleError<Cementing[]>(
              `getCementingsSqueezes HandoverId=${handoverId}`
            )
          );
      })
    );
  }

  saveCementingsSqueeze(squeezes: Squeeze[]): Observable<Squeeze[]> {
    const url = `${this.handoversUrl}/${squeezes[0].handoverId}/cementing/squeeze`;
    const squeezesToPost = squeezes.map((squeeze: Squeeze) => {
      return {
        handoverId: squeeze.handoverId,
        wellId: squeeze.wellId,
        wellboreId: squeeze.wellboreId,
        cementJobId: squeeze.cementJobId,
        cementStageId: squeeze.cementStageId,
        comments: squeeze.comments ?? '',
      };
    });
    return this.http.post<Squeeze[]>(url, squeezesToPost).pipe(
      tap((squeeze: Squeeze[]) => {
        console.log(`Cementings Squeeze saved! ${squeeze}`);
      }),
      catchError(this.handleError<Squeeze[]>('saveCementingsSqueeze'))
    );
  }

  getCementingsPlugs(
    handoverId: number | undefined,
    wellId: string,
    handoverStatusId: number,
    handoverTypeId: number | undefined
  ): Observable<Plug[]> {
    const url = `${this.handoversUrl}/${handoverId}/cementing/plug?wellId=${wellId}&handoverStatusId=${handoverStatusId}&handoverTypeId=${handoverTypeId}`;
    return this.http.get<Plug[]>(url).pipe(
      tap((p: Plug[]) => {
        console.log(
          `HandoverId ${handoverId} - Cementings Squeeze: ${p.length} fetched`
        ),
          catchError(
            this.handleError<Plug[]>(
              `getCementingsPlugs HandoverId=${handoverId}`
            )
          );
      })
    );
  }

  saveCementingsPlug(plugs: Plug[]): Observable<Plug[]> {
    const url = `${this.handoversUrl}/${plugs[0].handoverId}/cementing/plug`;
    const plugsToPost = plugs.map((plug: Plug) => {
      return {
        handoverId: plug.handoverId,
        wellId: plug.wellId,
        wellboreId: plug.wellboreId,
        cementJobId: plug.cementJobId,
        cementStageId: plug.cementStageId,
        comments: plug.comments ?? '',
      };
    });
    return this.http.post<Plug[]>(url, plugsToPost).pipe(
      tap((plug: Plug[]) => {
        console.log(`Cementings Plug saved! ${plug}`);
      }),
      catchError(this.handleError<Plug[]>('saveCementingsPlug'))
    );
  }

  /* Annulus Spaces */

  getAnnulusSpaces(
    handoverId: number,
    wellId: string,
    handoverStatusId: number,
    handoverTypeId: number
  ): Observable<AnnulusSpace[]> {
    const url = `${this.handoversUrl}/${handoverId}/annulus-spaces?wellId=${wellId}&handoverStatusId=${handoverStatusId}&handoverTypeId=${handoverTypeId}`;
    return this.http.get<AnnulusSpace[]>(url).pipe(
      tap((c: AnnulusSpace[]) => {
        console.log(
          `HandoverId ${handoverId} - AnnulusSpaces: ${c.length} fetched`
        );
      }),
      catchError(
        this.handleError<AnnulusSpace[]>(
          `getAnnulusSpaces HandoverId=${handoverId}`
        )
      )
    );
  }

  saveAnnulusSpaces(annulusSpaces: AnnulusSpace[]): Observable<AnnulusSpace[]> {
    const url = `${this.handoversUrl}/${annulusSpaces[0].handoverId}/annulus-spaces`;
    const spacesToPost = annulusSpaces.map((space: AnnulusSpace) => {
      return {
        handoverId: space.handoverId,
        wellId: space.wellId,
        numAnular: space.numAnular,
        hasMeasuredPressure: space.hasMeasuredPressure,
        pressureMeasureDate: space.pressureMeasureDate
          ? formatDate(
              space.pressureMeasureDate,
              'yyyy-MM-dd HH:mm:ss',
              this.locale
            )
          : null,
        measuredPressure: space.measuredPressure,
        annulusFluidBaseId: space.annulusFluidBaseId,
        averageDensity: space.averageDensity,
        comments: space.comments ?? '',
      };
    });
    return this.http.post<[]>(url, spacesToPost).pipe(
      tap((space: AnnulusSpace[]) => {
        console.log(`AnnulusSpaces saved! ${space}`);
      }),
      catchError(this.handleError<AnnulusSpace[]>('saveAnnulusSpaces'))
    );
  }

  getAnnulusFluidBases(): Observable<AnnulusFluidBase[]> {
    const url = `${this.dhoApiUrl}/annulus-fluid-bases`;
    return this.http.get<AnnulusFluidBase[]>(url).pipe(
      tap((b: AnnulusFluidBase[]) => {
        console.log(`AnnulusFluidBases: ${b.length} fetched`);
      }),
      catchError(this.handleError<AnnulusFluidBase[]>('getAnnulusFluidBases'))
    );
  }

  /* Pozos Inyectores (subsección de Espacios Anulares) */

  getAnnulusInjector(handoverId: number): Observable<AnnulusInjector> {
    const url = `${this.handoversUrl}/${handoverId}/annulus-injector`;
    return this.http.get<AnnulusInjector>(url).pipe(
      tap(() => {
        console.log(`HandoverId ${handoverId} - AnnulusInjector fetched`);
      }),
      catchError(
        this.handleError<AnnulusInjector>(
          `getAnnulusInjector HandoverId=${handoverId}`
        )
      )
    );
  }

  saveAnnulusInjector(
    handoverId: number,
    annulusInjector: AnnulusInjector
  ): Observable<AnnulusInjector> {
    const url = `${this.handoversUrl}/${handoverId}/annulus-injector`;
    const injectorToPost = {
      pressures: annulusInjector.pressures.map(
        (pressure: AnnulusInjectorPressure) => {
          return {
            handoverId: handoverId,
            annularTypeId: pressure.annularTypeId,
            fluidDensity: pressure.fluidDensity,
            maxSurfacePressure: pressure.maxSurfacePressure,
          };
        }
      ),
      casingLoss: {
        handoverId: handoverId,
        injectionPressure: annulusInjector.casingLoss.injectionPressure,
        maxThicknessLossPercentage:
          annulusInjector.casingLoss.maxThicknessLossPercentage,
      },
    };
    return this.http.post<AnnulusInjector>(url, injectorToPost).pipe(
      tap(() => {
        console.log(`AnnulusInjector saved!`);
      }),
      catchError(this.handleError<AnnulusInjector>('saveAnnulusInjector'))
    );
  }

  /* Wellheads */

  getWellheadSectionNames(): Observable<WellheadSectionName[]> {
    const url = `${this.dhoApiUrl}/wellhead-section-names`;
    return this.http.get<WellheadSectionName[]>(url).pipe(
      tap((b: WellheadSectionName[]) => {
        console.log(`WellheadSectionName: ${b.length} fetched`);
      }),
      catchError(
        this.handleError<WellheadSectionName[]>('getWellheadSectionNames')
      )
    );
  }

  getWellheadTopConnectorTypes(): Observable<WellheadTopConnectorType[]> {
    const url = `${this.dhoApiUrl}/wellhead-top-connector-types`;
    return this.http.get<WellheadTopConnectorType[]>(url).pipe(
      tap((b: WellheadTopConnectorType[]) => {
        console.log(`WellheadTopConnectorType: ${b.length} fetched`);
      }),
      catchError(
        this.handleError<WellheadTopConnectorType[]>(
          'getWellheadTopConnectorTypes'
        )
      )
    );
  }

  getWellheadBottomConnectorTypes(): Observable<WellheadBottomConnectorType[]> {
    const url = `${this.dhoApiUrl}/wellhead-bottom-connector-types`;
    return this.http.get<WellheadBottomConnectorType[]>(url).pipe(
      tap((b: WellheadBottomConnectorType[]) => {
        console.log(`WellheadBottomConnectorType: ${b.length} fetched`);
      }),
      catchError(
        this.handleError<WellheadBottomConnectorType[]>(
          'getWellheadBottomConnectorTypes'
        )
      )
    );
  }

  getWellheads(handoverId: number, wellId: string): Observable<Wellhead[]> {
    const url = `${this.handoversUrl}/${handoverId}/wellheads?wellId=${wellId}`;
    return this.http.get<Wellhead[]>(url).pipe(
      tap((c: Wellhead[]) => {
        console.log(
          `HandoverId ${handoverId} - Wellheads: ${c.length} fetched`
        );
      }),
      catchError(
        this.handleError<Wellhead[]>(`getWellheads HandoverId=${handoverId}`)
      )
    );
  }

  saveWellheads(wellheads: Wellhead[]): Observable<Wellhead[]> {
    const url = `${this.handoversUrl}/${wellheads[0].handoverId}/wellheads`;
    const wellheadsToPost = wellheads.map((wh: Wellhead) => {
      return {
        handoverId: wh.handoverId,
        wellId: wh.wellId,
        wellboreId: wh.wellboreId,
        assemblyId: wh.assemblyId,
        casingId: wh.casingId,
        sectionNameId: wh.sectionNameId,
        topConnectorTypeId: wh.topConnectorTypeId,
        bottomConnectorTypeId: wh.bottomConnectorTypeId,
        comments: wh.comments ?? '',
      };
    });
    return this.http.post<[]>(url, wellheadsToPost).pipe(
      tap((savedWellheads: Wellhead[]) => {
        console.log(`Wellheads saved! ${savedWellheads}`);
      }),
      catchError(this.handleError<Wellhead[]>('saveWellheads'))
    );
  }

  getWellheadHydraulic(handoverId: number): Observable<WellheadHydraulic> {
    const url = `${this.handoversUrl}/${handoverId}/wellhead/hydraulic`;
    return this.http.get<WellheadHydraulic>(url).pipe(
      tap((c: WellheadHydraulic) => {
        console.log(`HandoverId ${handoverId} - Wellhead hydraulic fetched`);
      }),
      catchError(
        this.handleError<WellheadHydraulic>(
          `getWellheads HandoverId=${handoverId}`
        )
      )
    );
  }

  saveWellheadHydraulic(
    handoverId: number,
    wellheadHydraulic: WellheadHydraulic
  ): Observable<WellheadHydraulic> {
    const url = `${this.handoversUrl}/${handoverId}/wellhead/hydraulic`;
    const wellheadHydraulicToPost = {
      handoverId: handoverId,
      masterValve: wellheadHydraulic.masterValve,
      valveOverMaster: wellheadHydraulic.valveOverMaster,
    };

    return this.http.post<WellheadHydraulic>(url, wellheadHydraulicToPost).pipe(
      tap((savedWellheadHydraulic: WellheadHydraulic) => {
        console.log(`Wellhead Hydraulic saved! ${savedWellheadHydraulic}`);
      }),
      catchError(this.handleError<WellheadHydraulic>('saveWellheadHydraulic'))
    );
  }

  getWellheadImages(handoverId: number): Observable<Image[]> {
    const url = `${this.handoversUrl}/${handoverId}/wellheads/images`;
    return this.http.get<Image[]>(url).pipe(
      tap((c: Image[]) => {
        console.log(`HandoverId ${handoverId} - getWellheadImages fetched`);
      }),
      catchError(
        this.handleError<Image[]>(`getWellheadImages HandoverId=${handoverId}`)
      )
    );
  }

  /* Esquema de Cabezal */

  getWellheadSchemes(handoverId: number): Observable<WellheadScheme[]> {
    const url = `${this.handoversUrl}/${handoverId}/wellhead/schemes`;
    return this.http.get<WellheadScheme[]>(url).pipe(
      tap((s: WellheadScheme[]) => {
        console.log(
          `HandoverId ${handoverId} - WellheadSchemes: ${s.length} fetched`
        );
      }),
      catchError(
        this.handleError<WellheadScheme[]>(
          `getWellheadSchemes HandoverId=${handoverId}`
        )
      )
    );
  }

  /**
   * Reemplaza el conjunto completo de esquemas del handover.
   * Un array vacío borra todos los existentes.
   */
  saveWellheadSchemes(
    handoverId: number,
    schemes: WellheadScheme[]
  ): Observable<WellheadScheme[]> {
    const url = `${this.handoversUrl}/${handoverId}/wellhead/schemes`;
    const schemesToPost = schemes.map((scheme: WellheadScheme) => {
      return {
        sectionId: scheme.sectionId,
        workingPressure: scheme.workingPressure,
        testDate: scheme.testDate
          ? formatDate(scheme.testDate, 'yyyy-MM-dd HH:mm:ss', this.locale)
          : null,
      };
    });
    return this.http.post<WellheadScheme[]>(url, schemesToPost).pipe(
      tap(() => {
        console.log(`WellheadSchemes saved!`);
      }),
      catchError(this.handleError<WellheadScheme[]>('saveWellheadSchemes'))
    );
  }

  /* Well tree */

  getWellTree(
    handoverId: number,
    wellId: string,
    handoverStatusId: number
  ): Observable<WellTree[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-tree?wellId=${wellId}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<WellTree[]>(url).pipe(
      tap((wellTrees: WellTree[]) => {
        const totalComponents = wellTrees.reduce((total, wellTree) => {
          return total + wellTree.components.length;
        }, 0);
        console.log(
          `HandoverId ${handoverId} - Tree Components: ${totalComponents} fetched`
        );
      }),
      catchError(
        this.handleError<WellTree[]>(`getWellTree HandoverId=${handoverId}`)
      )
    );
  }

  saveWellTreeComponent(
    wellTreeComponents: WellTreeComponent[]
  ): Observable<WellTreeComponent[]> {
    const url = `${this.handoversUrl}/${wellTreeComponents[0].handoverId}/well-tree/component`;
    const componentsToPost = wellTreeComponents.map((wh: WellTreeComponent) => {
      return {
        handoverId: wh.handoverId,
        wellId: wh.wellId,
        wellheadId: wh.wellheadId,
        wellheadCompId: wh.wellheadCompId,
        compDescription: wh.compDescription,
        diamIntComp: wh.diamIntComp,
        sequenceNo: wh.sequenceNo,
        workingPressure: wh.workingPressure,
        manufacturer: wh.manufacturer,
        comments: wh.comments ?? '',
        valveState: wh.valveState,
        eventId: wh.eventId,
      };
    });
    return this.http.post<[]>(url, componentsToPost).pipe(
      tap((savedWellTreeComponents: WellTreeComponent[]) => {
        console.log(`Component saved! ${savedWellTreeComponents}`);
      }),
      catchError(this.handleError<WellTreeComponent[]>('saveWellTreeComponent'))
    );
  }

  getSuppliers(): Observable<Supplier[]> {
    const url = `${this.dhoApiUrl}/tree-suppliers`;
    return this.http.get<Supplier[]>(url).pipe(
      tap((b: Supplier[]) => {
        console.log(`Suppliers: ${b.length} fetched`);
      }),
      catchError(this.handleError<Supplier[]>('getSuppliers'))
    );
  }

  saveWellTree(wellTree: WellTree): Observable<WellTree> {
    const url = `${this.handoversUrl}/${wellTree.handoverId}/well-tree`;
    const treeToPost = {
      wellId: wellTree.wellId,
      wellheadId: wellTree.wellheadId,
      wellheadSize: wellTree.wellheadSize,
      designPressure: wellTree.designPressure,
      supplier: {
        supplierId: wellTree.supplier.supplierId,
        supplierName: wellTree.supplier.supplierName,
      },
    };
    return this.http.post<WellTree>(url, treeToPost).pipe(
      tap((savedWellTree: WellTree) => {
        console.log(`Component saved! ${savedWellTree}`);
      }),
      catchError(this.handleError<WellTree>('saveWellTreeComponent'))
    );
  }

  getWellTreeImages(handoverId: number): Observable<Image[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-tree/images`;
    return this.http.get<Image[]>(url).pipe(
      tap((c: Image[]) => {
        console.log(`HandoverId ${handoverId} - getWellTreeImages fetched`);
      }),
      catchError(
        this.handleError<Image[]>(`getWellTreeImages HandoverId=${handoverId}`)
      )
    );
  }

  getWellTreeComponentsVisibility(
    handoverId: number
  ): Observable<WellTreeComponentsVisibility> {
    const url = `${this.handoversUrl}/${handoverId}/well-tree/components-visibility`;
    return this.http.get<WellTreeComponentsVisibility>(url).pipe(
      tap((c: WellTreeComponentsVisibility) => {
        console.log(
          `HandoverId ${handoverId} - getWellTreeComponentsVisibility fetched`
        );
      }),
      catchError(
        this.handleError<WellTreeComponentsVisibility>(
          `getWellTreeComponentsVisibility HandoverId=${handoverId}`
        )
      )
    );
  }

  saveWellTreeComponentsVisibility(
    handoverId: number,
    componentsVisible: boolean
  ): Observable<boolean> {
    const url =
      `${this.handoversUrl}/${handoverId}/well-tree/components-visibility` +
      `?generalDataComponentsVisible=${componentsVisible}`;
    return this.http.post<boolean>(url, null).pipe(
      tap((saved: boolean) => {
        console.log(`Well Tree Component Visibility saved: ${saved}`);
      }),
      catchError(this.handleError<boolean>('saveWellTreeComponentsVisibility'))
    );
  }

  /* Perforation (Punzados) */

  getPerforations(
    handoverId: number,
    wellId: string,
    handoverStatusId: number
  ): Observable<Perforation[]> {
    const url = `${this.handoversUrl}/${handoverId}/perforation?wellId=${wellId}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<Perforation[]>(url).pipe(
      tap((d: Perforation[]) =>
        console.log(
          `HandoverId ${handoverId} - Perforations: ${d.length} fetched`
        )
      ),
      catchError(
        this.handleError<Perforation[]>(`getPerforations HO id=${handoverId}`)
      )
    );
  }

  /* Abandon */

  getAbandon(
    handoverId: number,
    wellId: string,
    handoverStatusId: number
  ): Observable<Abandon[]> {
    const url = `${this.handoversUrl}/${handoverId}/abandon?wellId=${wellId}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<Abandon[]>(url).pipe(
      tap((abandon: Abandon[]) => {
        console.log(
          `HandoverId ${handoverId} - Abandon: ${abandon.length} fetched`
        ),
          catchError(
            this.handleError<Cementing[]>(`getAbandon HandoverId=${handoverId}`)
          );
      })
    );
  }

  saveAbandon(abandon: Abandon[]): Observable<Abandon[]> {
    const url = `${this.handoversUrl}/${abandon[0].handoverId}/abandon`;
    const abandonToPost = abandon.map((aba: Abandon) => {
      return {
        handoverId: aba.handoverId,
        wellId: aba.wellId,
        wellboreId: aba.wellboreId,
        cementJobId: aba.cementJobId,
        cementStageId: aba.cementStageId,
        topCheck: aba.topCheck,
        mdTopChecked: aba.mdTopChecked,
        supportPlugType: aba.supportPlugType ?? '',
        mdSupportPlugTop: aba.mdSupportPlugTop,
        comment: aba.comment ?? '',
      };
    });
    return this.http.post<Abandon[]>(url, abandonToPost).pipe(
      tap((abandon: Abandon[]) => {
        console.log(`Abandon saved! ${abandon}`);
      }),
      catchError(this.handleError<Abandon[]>('saveAbandon'))
    );
  }

  /* Production Column */

  getProductionColumnHeaders(
    handoverId: number,
    wellId: string,
    handoverStatusId: number
  ): Observable<ProductionColumnHeaderResponse> {
    const url = `${this.handoversUrl}/${handoverId}/production-column-header?wellId=${wellId}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<ProductionColumnHeaderResponse>(url).pipe(
      tap((d: ProductionColumnHeaderResponse) =>
        console.log(
          `HandoverId ${handoverId} - ProductionColumnHeaders: ${d.headers.length} fetched`
        )
      ),
      catchError(
        this.handleError<ProductionColumnHeaderResponse>(
          `getProductionColumnHeader HO id=${handoverId}`
        )
      )
    );
  }

  saveProductionColumnHeaders(
    request: ProductionColumnHeaderRequest,
    handoverId: number,
    wellId: string
  ): Observable<ProductionColumnHeaderResponse> {
    const url = `${this.handoversUrl}/${handoverId}/production-column-header?wellId=${wellId}`;
    return this.http.post<ProductionColumnHeaderResponse>(url, request).pipe(
      tap((header: ProductionColumnHeaderResponse) =>
        console.log(`ProductionColumnHeaders saved! ${header}`)
      ),
      catchError(
        this.handleError<ProductionColumnHeaderResponse>(
          `saveProductionColumnHeaders`
        )
      )
    );
  }

  getProductionColumns(
    handoverId: number,
    wellId: string,
    handoverStatusId: number
  ): Observable<ProductionColumn[]> {
    const url = `${this.handoversUrl}/${handoverId}/production-column?wellId=${wellId}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<ProductionColumn[]>(url).pipe(
      tap((d: ProductionColumn[]) =>
        console.log(
          `HandoverId ${handoverId} - ProductionColumns: ${d.length} fetched`
        )
      ),
      catchError(
        this.handleError<ProductionColumn[]>(
          `getProductionColumn HO id=${handoverId}`
        )
      )
    );
  }

  saveProductionColumns(
    columns: ProductionColumn[],
    handoverId: number,
    wellId: string
  ): Observable<ProductionColumn[]> {
    const url = `${this.handoversUrl}/${handoverId}/production-column?wellId=${wellId}`;
    // Filter only the data that need to save.
    const columnsToPost = columns.map((value: ProductionColumn) => {
      return {
        handoverId: value.handoverId,
        wellId: value.wellId,
        wellboreId: value.wellboreId,
        assemblyId: value.assemblyId,
        assemblyCompId: value.assemblyCompId,
        comments: value.comments,
      };
    });
    return this.http.post<ProductionColumn[]>(url, columnsToPost).pipe(
      tap((columns: ProductionColumn[]) =>
        console.log(`ProductionColumns saved! ${columns}`)
      ),
      catchError(this.handleError<ProductionColumn[]>(`saveProductionColumns`))
    );
  }

  // Get Handovers Home
  getHandoversHome(
    limit = 10,
    offset = 0,
    sortDirection = 'desc'
  ): Observable<ResultHandoversHome> {
    const url = `${this.handoversHomeUrl}/?limit=${limit}&offset=${offset}&orderBy=${sortDirection}`;
    return this.http.get<ResultHandoversHome>(url).pipe(
      tap((_) => console.log('fetched handovers')),
      catchError(
        this.handleError<ResultHandoversHome>('getHandovers', {
          handoversGeneral: {} as ResultHandovers,
          handoversSentPersonal: {} as ResultHandovers,
          handoversEdition: {} as ResultHandovers,
          handoversInboxPersonal: {} as ResultHandovers,
          handoversRejectedPersonal: {} as ResultHandovers,
          handoversInboxTeam: {} as ResultHandovers,
          handoversInboxEditionTeam: {} as ResultHandovers,
          handoversSentTeam: {} as ResultHandovers,
          handoversRejectedTeam: {} as ResultHandovers,
        })
      )
    );
  }

  getHandoversHomeGeneral(
    limit = 10,
    offset = 0,
    sortDirection = 'desc',
    search: string
  ): Observable<ResultHandovers> {
    const url = `${this.handoversHomeUrl}/general?limit=${limit}&offset=${offset}&orderBy=${sortDirection}&search=${search}`;
    return this.http.get<ResultHandovers>(url).pipe(
      tap((_) => console.log('fetched handovers home general')),
      catchError(this.handleError<ResultHandovers>('getHandoversHomeGeneral'))
    );
  }

  getHandoversHomeInboxPersonal(
    limit = 10,
    offset = 0,
    sortDirection = 'desc',
    search: string
  ): Observable<ResultHandovers> {
    const url = `${this.handoversHomeUrl}/inbox/personal?limit=${limit}&offset=${offset}&orderBy=${sortDirection}&search=${search}`;
    return this.http.get<ResultHandovers>(url).pipe(
      tap((_) => console.log('fetched handovers home inbox personal')),
      catchError(
        this.handleError<ResultHandovers>('getHandoversHomeInboxPersonal')
      )
    );
  }

  getHandoversHomeInboxTeam(
    limit = 10,
    offset = 0,
    sortDirection = 'desc',
    search: string
  ): Observable<ResultHandovers> {
    const url = `${this.handoversHomeUrl}/inbox/team?limit=${limit}&offset=${offset}&orderBy=${sortDirection}&search=${search}`;
    return this.http.get<ResultHandovers>(url).pipe(
      tap((_) => console.log('fetched handovers home inbox Team')),
      catchError(this.handleError<ResultHandovers>('getHandoversHomeInboxTeam'))
    );
  }

  getHandoversHomeEditionPersonal(
    limit = 10,
    offset = 0,
    sortDirection = 'desc',
    search: string
  ): Observable<ResultHandovers> {
    const url = `${this.handoversHomeUrl}/edition/personal?limit=${limit}&offset=${offset}&orderBy=${sortDirection}&search=${search}`;
    return this.http.get<ResultHandovers>(url).pipe(
      tap((_) => console.log('fetched handovers home edition personal')),
      catchError(
        this.handleError<ResultHandovers>('getHandoversHomeEditionPersonal')
      )
    );
  }

  getHandoversHomeEditionTeam(
    limit = 10,
    offset = 0,
    sortDirection = 'desc',
    search: string
  ): Observable<ResultHandovers> {
    const url = `${this.handoversHomeUrl}/edition/team?limit=${limit}&offset=${offset}&orderBy=${sortDirection}&search=${search}`;
    return this.http.get<ResultHandovers>(url).pipe(
      tap((_) => console.log('fetched handovers home edition team')),
      catchError(
        this.handleError<ResultHandovers>('getHandoversHomeEditionTeam')
      )
    );
  }

  getHandoversHomeSentPersonal(
    limit = 10,
    offset = 0,
    sortDirection = 'desc',
    search: string
  ): Observable<ResultHandovers> {
    const url = `${this.handoversHomeUrl}/sent/personal?limit=${limit}&offset=${offset}&orderBy=${sortDirection}&search=${search}`;
    return this.http.get<ResultHandovers>(url).pipe(
      tap((_) => console.log('fetched handovers home sent personal')),
      catchError(
        this.handleError<ResultHandovers>('getHandoversHomeSentPersonal')
      )
    );
  }

  getHandoversHomeRejectedPersonal(
    limit = 10,
    offset = 0,
    sortDirection = 'desc',
    search: string
  ): Observable<ResultHandovers> {
    const url = `${this.handoversHomeUrl}/rejected/personal?limit=${limit}&offset=${offset}&orderBy=${sortDirection}&search=${search}`;
    return this.http.get<ResultHandovers>(url).pipe(
      tap((_) => console.log('fetched handovers home rejected personal')),
      catchError(
        this.handleError<ResultHandovers>('getHandoversHomeRejectedPersonal')
      )
    );
  }

  getHandoversHomeSentTeam(
    limit = 10,
    offset = 0,
    sortDirection = 'desc',
    search: string
  ): Observable<ResultHandovers> {
    const url = `${this.handoversHomeUrl}/sent/team?limit=${limit}&offset=${offset}&orderBy=${sortDirection}&search=${search}`;
    return this.http.get<ResultHandovers>(url).pipe(
      tap((_) => console.log('fetched handovers home sent team')),
      catchError(this.handleError<ResultHandovers>('getHandoversHomeSentTeam'))
    );
  }

  getHandoversHomeRejectedTeam(
    limit = 10,
    offset = 0,
    sortDirection = 'desc',
    search: string
  ): Observable<ResultHandovers> {
    const url = `${this.handoversHomeUrl}/rejected/team?limit=${limit}&offset=${offset}&orderBy=${sortDirection}&search=${search}`;
    return this.http.get<ResultHandovers>(url).pipe(
      tap((_) => console.log('fetched handovers home rejected team')),
      catchError(
        this.handleError<ResultHandovers>('getHandoversHomeRejectedTeam')
      )
    );
  }

  /* Schematic */

  getSchematicImages(handoverId: number): Observable<Image[]> {
    const url = `${this.handoversUrl}/${handoverId}/schematic/images`;
    return this.http.get<Image[]>(url).pipe(
      tap((c: Image[]) => {
        console.log(`HandoverId ${handoverId} - getSchematicImages fetched`);
      }),
      catchError(
        this.handleError<Image[]>(`getSchematicImages HandoverId=${handoverId}`)
      )
    );
  }

  /* Sections Comments */

  getSectionsComments(handoverId: number): Observable<SectionsComments[]> {
    const url = `${this.handoversUrl}/${handoverId}/sections/comments`;
    return this.http.get<SectionsComments[]>(url).pipe(
      tap((sc: SectionsComments[]) => {
        console.log(
          `HandoverId ${handoverId} - Sections Comments: ${sc.length} fetched`
        ),
          catchError(
            this.handleError<SectionsComments[]>(
              `getSectionsComments HandoverId=${handoverId}`
            )
          );
      })
    );
  }

  getSectionComment(
    handoverId: number,
    sectionId: number
  ): Observable<SectionsComments> {
    const url = `${this.handoversUrl}/${handoverId}/sections/comments/${sectionId}`;
    return this.http.get<SectionsComments>(url).pipe(
      tap((sc: SectionsComments) => {
        console.log(
          `HandoverId ${handoverId} - Section ${sc.handoverSectionId} comment fetched`
        ),
          catchError(
            this.handleError<SectionsComments>(
              `getSectionComment HandoverId=${handoverId} SectionId=${sectionId}`
            )
          );
      })
    );
  }

  getSections(handoverId: number): Observable<Section[]> {
    const url = `${this.handoversUrl}/${handoverId}/sections`;
    return this.http.get<Section[]>(url).pipe(
      tap((sections: Section[]) => {
        console.log(
          `HandoverId ${handoverId} - Sections: ${sections.length} fetched`
        ),
          catchError(
            this.handleError<Section[]>(`getSections HandoverId=${handoverId}`)
          );
      })
    );
  }

  saveSectionComment(
    sectionComment: SectionComment
  ): Observable<SectionComment> {
    const url = `${this.handoversUrl}/${sectionComment.handoverId}/sections/comments`;
    return this.http.post<SectionComment>(url, sectionComment).pipe(
      tap((sectionComment: SectionComment) => {
        console.log(`Section Comment saved! ${sectionComment}`);
      }),
      catchError(this.handleError<SectionComment>('saveSectionComment'))
    );
  }

  deleteSectionComment(
    handoverId: number,
    handoverSectionId: number
  ): Observable<boolean> {
    const url = `${this.handoversUrl}/${handoverId}/sections/${handoverSectionId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((res: boolean) => {
        console.log(`Section Comment deleted! ${res}`);
      }),
      catchError(this.handleError<boolean>('deleteSectionComment'))
    );
  }

  /**
   * Validates if the Ho content of seccions can be editable o not.
   * @param hoStatusId A valid Ho status Id, associated with the communication sequences of the HO document.
   * @param userLoggedIn The user that created the Ho Document.
   * @param emitterId The user who started the session.
   * @returns boolean: True = content needs to be readonly. False = content needs to be editable.
   */
  validateHOViewOnlyState(
    hoStatusId: number,
    handoverTypeId: number,
    isEngineeringUser: boolean,
    userLoggedIn?: number,
    emitterId?: number,
    editors?: number[],
    sentUser?: boolean,
    sentEngineeringUser?: boolean
  ): boolean {
    let viewOnlyState = true;

    //Validates if the Ho statusId is an editable state
    if (handoverTypeId == HANDOVER_TYPE_HO3_ID) {
      if (
        userLoggedIn != null &&
        editors?.includes(userLoggedIn) &&
        HANDOVER_STATUSES_EDITABLES_HO3.includes(hoStatusId) &&
        ((isEngineeringUser && !sentEngineeringUser) ||
          (!isEngineeringUser && !sentUser))
      ) {
        viewOnlyState = false;
      }
    } else {
      if (
        userLoggedIn != null &&
        HANDOVER_STATUSES_EDITABLES.includes(hoStatusId) &&
        emitterId != null &&
        userLoggedIn == emitterId
      ) {
        viewOnlyState = false;
      }
    }

    return viewOnlyState;
  }

  /* Well Integrity */

  getBarriersSchemaImages(handoverId: number): Observable<Image[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-integrity/barrier-schema`;
    return this.http.get<Image[]>(url).pipe(
      tap((c: Image[]) => {
        console.log(
          `HandoverId ${handoverId} - getBarriersSchemaImages fetched`
        );
      }),
      catchError(
        this.handleError<Image[]>(
          `getBarriersSchemaImages HandoverId=${handoverId}`
        )
      )
    );
  }

  getBarrierElements(ho3 = false): Observable<BarrierElement[]> {
    const url = `${this.dhoApiUrl}/barrier-element` + (ho3 ? '-ho3' : '');
    return this.http.get<BarrierElement[]>(url).pipe(
      tap((c: BarrierElement[]) => {
        console.log(`getBarrierElements fetched`);
      }),
      catchError(this.handleError<BarrierElement[]>(`getBarrierElements`))
    );
  }

  getBarrierElementProblems(
    handoverId: number
  ): Observable<BarrierElementProblem[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-integrity/barrier-element-problem`;
    return this.http.get<BarrierElementProblem[]>(url).pipe(
      tap((c: BarrierElementProblem[]) => {
        console.log(
          `HandoverId ${handoverId} - getBarrierElementProblems fetched`
        );
      }),
      catchError(
        this.handleError<BarrierElementProblem[]>(
          `getBarrierElementProblems HandoverId=${handoverId}`
        )
      )
    );
  }

  saveBarrierElementProblem(
    handoverId: number,
    problem: BarrierElementProblem
  ): Observable<BarrierElementProblem> {
    const url = `${this.handoversUrl}/${handoverId}/well-integrity/barrier-element-problem`;
    return this.http.post<BarrierElementProblem>(url, [problem]).pipe(
      tap((header: BarrierElementProblem) =>
        console.log(`BarrierElementProblem saved! ${header}`)
      ),
      catchError(
        this.handleError<BarrierElementProblem>(`saveBarrierElementProblem`)
      )
    );
  }

  deleteBarrierElementProblem(
    handoverId: number,
    problem: BarrierElementProblem
  ): Observable<boolean> {
    const url = `${this.handoversUrl}/${handoverId}/well-integrity/barrier-element-problem/?barrierElementId=${problem.element.barrierElementId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((res: boolean) => {
        console.log(`Barrier Element Problem deleted: ${res}`);
      }),
      catchError(this.handleError<boolean>('deleteBarrierElementProblem'))
    );
  }

  getTubings(): Observable<Tubing[]> {
    const url = `${this.dhoApiUrl}/tubing`;
    return this.http.get<Tubing[]>(url).pipe(
      tap((t: Tubing[]) => {
        console.log(`getTubings fetched`);
      }),
      catchError(this.handleError<Tubing[]>(`getTubings`))
    );
  }

  getTubingWearReports(handoverId: number): Observable<TubingWearReport[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-integrity/tubing-wear-report`;
    return this.http.get<TubingWearReport[]>(url).pipe(
      tap((r: TubingWearReport[]) => {
        console.log(`HandoverId ${handoverId} - getTubingWearReports fetched`);
      }),
      catchError(
        this.handleError<TubingWearReport[]>(
          `getTubingWearReports HandoverId=${handoverId}`
        )
      )
    );
  }

  saveTubingWearReports(
    handoverId: number,
    reports: TubingWearReport[]
  ): Observable<TubingWearReport[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-integrity/tubing-wear-report`;
    return this.http.post<TubingWearReport[]>(url, reports).pipe(
      tap((report: TubingWearReport[]) =>
        console.log(`TubingWearReport[] saved! ${report}`)
      ),
      catchError(this.handleError<TubingWearReport[]>(`saveTubingWearReport`))
    );
  }

  deleteTubingWearReport(
    handoverId: number,
    report: TubingWearReport
  ): Observable<boolean> {
    const url = `${this.handoversUrl}/${handoverId}/well-integrity/tubing-wear-report/?tubingId=${report.tubing.tubingId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((res: boolean) => {
        console.log(`Tubing Wear Report deleted: ${res}`);
      }),
      catchError(this.handleError<boolean>('deleteTubingWearReport'))
    );
  }

  /* Status and Notifications */

  // getWellEvents is used in Status section
  getWellEvents(
    handoverId: number,
    wellId: string,
    handoverStatusId: number
  ): Observable<WellEvent[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-event?wellId=${wellId}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<WellEvent[]>(url).pipe(
      tap((cl: WellEvent[]) =>
        console.log(
          `HandoverId ${handoverId} - Status/Events ${cl.length} fetched`
        )
      ),
      catchError(
        this.handleError<WellEvent[]>(`getWellEvents HO id=${handoverId}`)
      )
    );
  }

  // getWellStatusProduction is used in Status section
  getWellStatusProduction(
    handoverId: number,
    uwi: string,
    handoverStatusId: number
  ): Observable<WellStatusProduction[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-status-production?uwi=${uwi}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<WellStatusProduction[]>(url).pipe(
      tap((cl: WellStatusProduction[]) =>
        console.log(`HandoverId ${handoverId} - Status ${cl.length} fetched`)
      ),
      catchError(
        this.handleError<WellStatusProduction[]>(
          `getWellStatusProduction HO id=${handoverId}`
        )
      )
    );
  }

  saveHandoverEvent(
    wellEvent: WellEvent,
    handoverId: number,
    wellId: string
  ): Observable<WellEvent[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-event?wellId=${wellId}`;

    wellEvent.handoverId = handoverId;
    return this.http.post<WellEvent[]>(url, [wellEvent]).pipe(
      tap((header: WellEvent[]) => console.log(`WellEvent saved! ${header}`)),
      catchError(this.handleError<WellEvent[]>(`saveHandoverEvent`))
    );
  }

  // getWellTypes is used in Notifications section
  getWellTypes(handoverId: number): Observable<WellType[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-type`;
    return this.http.get<WellType[]>(url).pipe(
      tap((types: WellType[]) =>
        console.log(`Well Types ${types.length} fetched`)
      ),
      catchError(this.handleError<WellType[]>(`getWellTypes`))
    );
  }

  // saveWellType is used in Notifications section
  saveWellType(
    handoverId: number,
    wellTypeId: number,
    otherValue: string
  ): Observable<boolean> {
    let url = `${this.handoversUrl}/${handoverId}/well-type?wellTypeId=${wellTypeId}`;
    if (otherValue) {
      url += `&otherValue='${otherValue}'`;
    }
    return this.http.post<boolean>(url, null).pipe(
      tap((hasBeenSaved: boolean) =>
        console.log(`WellType ${hasBeenSaved ? '' : 'not '}saved!`)
      ),
      catchError(this.handleError<boolean>(`saveWellType`))
    );
  }

  // getNotifications is used in Notifications section
  getNotifications(
    handoverId: number,
    wellTypeId: number | null
  ): Observable<Notification[]> {
    let url = `${this.handoversUrl}/${handoverId}/notification`;
    if (wellTypeId) {
      url += `?wellTypeId=${wellTypeId}`;
    }
    return this.http.get<Notification[]>(url).pipe(
      tap((notifications: Notification[]) =>
        console.log(
          `Handover ${handoverId} - ${notifications.length} Notifications fetched`
        )
      ),
      catchError(this.handleError<Notification[]>(`getNotifications`))
    );
  }

  // getHandoverNotification is used in Notifications section
  getHandoverNotification(
    handoverId: number,
    notificationId: number
  ): Observable<Notification> {
    const url = `${this.handoversUrl}/${handoverId}/notification/${notificationId}`;
    return this.http.get<Notification>(url).pipe(
      tap((notification: Notification) =>
        console.log(
          `Handover ${handoverId} - Notification ${notification.notificationId} fetched`
        )
      ),
      catchError(this.handleError<Notification>(`getHandoverNotification`))
    );
  }

  // saveNotifications is used in Notifications section
  saveNotifications(
    handoverId: number,
    notificationsToSave: NotificationValue[],
    deleteNotifications: boolean
  ): Observable<boolean> {
    const url = `${this.handoversUrl}/${handoverId}/notification?deleteNotifications=${deleteNotifications}`;
    return this.http.post<boolean>(url, notificationsToSave).pipe(
      tap((haveBeenSaved: boolean) =>
        console.log(`Notifications ${haveBeenSaved ? '' : 'not '}saved!`)
      ),
      catchError(this.handleError<boolean>(`saveWellType`))
    );
  }

  saveNotificationAttachment(
    handoverId: number,
    notificationId: number,
    formData: FormData
  ): Observable<NotificationAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/notification/attachment?notificationId=${notificationId}`;
    return this.http.post<NotificationAttachment[]>(url, formData).pipe(
      tap((_) => console.log('notification attachment created')),
      catchError((error) => {
        this.handleError<NotificationAttachment[]>(
          'saveNotificationAttachment',
          []
        );
        return throwError(error);
      })
    );
  }

  deleteNotificationAttachment(
    handoverId: number,
    notificationId: number,
    fileId: number,
    filePath: string
  ): Observable<boolean> {
    const url =
      `${this.handoversUrl}/${handoverId}/notification/attachment?` +
      `notificationId=${notificationId}&fileId=${fileId}&filePath=${filePath}`;
    return this.http.delete<boolean>(url).pipe(
      tap((_) => console.log('notification attachment deleted')),
      catchError((error) => {
        this.handleError<boolean>('deleteNotificationAttachment', false);
        return throwError(error);
      })
    );
  }

  getHandoversHistory(
    wellId: string,
    handoverReturnRows: number | undefined
  ): Observable<HandoversHistory> {
    let url = '';
    if (handoverReturnRows) {
      url = `${this.handoversUrl}/history/${wellId}?handoverReturnRows=${handoverReturnRows}`;
    } else {
      url = `${this.handoversUrl}/history/${wellId}`;
    }
    return this.http.get<HandoversHistory>(url).pipe(
      tap((hh: HandoversHistory) => {
        console.log(
          `WellId ${wellId} - History Handovers: ${hh.handoversListDetails.length} fetched, total: ${hh.totalAmountOfHandovers}`
        ),
          catchError(
            this.handleError<HandoversHistory>(
              `getHandoversHistory wellId=${wellId}`
            )
          );
      })
    );
  }

  getHandoversTotalCount(): Observable<HandoversTotalCount> {
    const url = `${this.handoversUrl}/search/total-count`;
    return this.http.get<HandoversTotalCount>(url).pipe(
      tap((t: HandoversTotalCount) => {
        console.log(`Handovers Total Count: ${t.totalAmount}`),
          catchError(
            this.handleError<HandoversTotalCount>(`getHandoversTotalCount`)
          );
      })
    );
  }

  getSectionsCompletion(
    handoverId: number,
    wellId: string,
    handoverTypeId: number
  ): Observable<SectionsCompletion> {
    const url = `${this.handoversUrl}/${handoverId}/sections-completion?wellId=${wellId}&handoverTypeId=${handoverTypeId}`;
    return this.http.get<SectionsCompletion>(url).pipe(
      tap((completion: SectionsCompletion) => {
        console.log(
          `HandoverId ${handoverId} - Sections Completed: ${completion.completed}`
        ),
          catchError(
            this.handleError<SectionsCompletion>(
              `getSectionsCompletion HandoverId=${handoverId}`
            )
          );
      })
    );
  }

  getValidReceiversCount(
    ugId: number,
    areaId: number,
    distrctId: number | null
  ): Observable<HandoverValidReceiversCount> {
    let url = `${this.handoversUrl}/valid-receivers/count?ugId=${ugId}&areaId=${areaId}`;

    if (distrctId != null) {
      url = `${this.handoversUrl}/valid-receivers/count?ugId=${ugId}&areaId=${areaId}&districtId=${distrctId}`;
    }

    return this.http.get<HandoverValidReceiversCount>(url).pipe(
      tap((hvr: HandoverValidReceiversCount) => {
        console.log(`Available Receiver Count: ${hvr.avaiablesReceivers}`),
          catchError(
            this.handleError<HandoverValidReceiversCount>(
              `getValidReceiversCount`
            )
          );
      })
    );
  }

  owValidation(
    uwi: string,
    hoValidationType: string
  ): Observable<HandoverOwValidation> {
    const url = `${this.dhoApiUrl}/openwells/validation?hoValType=${hoValidationType}&wellUwi=${uwi}`;
    return this.http.get<HandoverOwValidation>(url).pipe(
      tap((howv: HandoverOwValidation) => {
        console.log(`owValidationRules: ${howv.validationResult}`),
          catchError(
            this.handleError<HandoverOwValidation>(`owValidationRules`)
          );
      })
    );
  }

  getHandoverInitiateValidationType(
    handoverTypeId: number,
    fromAreaId: number
  ): string {
    let validationType = '';
    switch (handoverTypeId) {
      case HANDOVER_TYPE_HOG_ID:
        validationType = HANDOVER_INITIATE_VALIDATION_TYPE_HOG;
        break;
      case HANDOVER_TYPE_HOG_LOC_ID:
        validationType = HANDOVER_INITIATE_VALIDATION_TYPE_HOG;
        break;
      case HANDOVER_TYPE_HO1_ID:
        validationType = HANDOVER_INITIATE_VALIDATION_TYPE_HO1;
        break;
      case HANDOVER_TYPE_HO1_LOC_ID:
        validationType = HANDOVER_INITIATE_VALIDATION_TYPE_HO1_LOC;
        break;
      case HANDOVER_TYPE_HO2_ID:
        validationType = HANDOVER_INITIATE_VALIDATION_TYPE_HO2;
        break;
      case HANDOVER_TYPE_HO2_LOC_ID:
        validationType = HANDOVER_INITIATE_VALIDATION_TYPE_HO2_LOC;
        break;
      case HANDOVER_TYPE_HO4_ID:
        if (fromAreaId == OPERATIONAL_AREA_PULLING_ID) {
          validationType = HANDOVER_INITIATE_VALIDATION_TYPE_HO4_PULLING;
        } else if (fromAreaId == OPERATIONAL_AREA_COMPLETION_ID) {
          validationType = HANDOVER_INITIATE_VALIDATION_TYPE_HO4_WORKOVER;
        }
        break;
      case HANDOVER_TYPE_HO4_LOC_ID:
        if (fromAreaId == OPERATIONAL_AREA_COMPLETION_ID) {
          validationType = HANDOVER_INITIATE_VALIDATION_TYPE_HO4_WORKOVER_LOC;
        }
        break;
      case HANDOVER_TYPE_HO5_ID:
        validationType = HANDOVER_INITIATE_VALIDATION_TYPE_HO5;
        break;
      case HANDOVER_TYPE_HO5_LOC_ID:
        validationType = HANDOVER_INITIATE_VALIDATION_TYPE_HO5_LOC;
        break;
    }
    return validationType;
  }

  /* Mandriles */

  getMandrils(
    handoverId: number,
    wellId: string,
    wellUwi: string,
    handoverStatusId: number
  ): Observable<Mandril[]> {
    const url = `${this.handoversUrl}/${handoverId}/mandrils?wellId=${wellId}&wellUwi=${wellUwi}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<Mandril[]>(url).pipe(
      tap((c: Mandril[]) => {
        console.log(`HandoverId ${handoverId} - Mandrils: ${c.length} fetched`);
      }),
      catchError(
        this.handleError<Mandril[]>(`getMandrils HandoverId=${handoverId}`)
      )
    );
  }

  saveMandrils(mandrils: Mandril[]): Observable<Mandril[]> {
    const url = `${this.handoversUrl}/${mandrils[0].handoverId}/mandrils`;
    const mandrilsToPost = mandrils.map((mandril: Mandril) => {
      return {
        handoverId: mandril.handoverId,
        wellId: mandril.wellId,
        numMandril: mandril.numMandril,
        comments: mandril.comments ?? '',
      };
    });
    return this.http.post<[]>(url, mandrilsToPost).pipe(
      tap((mandril: Mandril[]) => {
        console.log(`Mandrils saved! ${mandril}`);
      }),
      catchError(this.handleError<Mandril[]>('saveMandrils'))
    );
  }

  /*Acta de Locacion */

  getDeliveryLocations(
    handoverId: number,
    managementUnitId: number,
    handoverTypeAreaReasonId: number
  ): Observable<DeliveryLocation[]> {
    const url = `${this.handoversUrl}/${handoverId}/delivery-location?managementUnitId=${managementUnitId}&handoverTypeAreaReasonId=${handoverTypeAreaReasonId}`;
    return this.http.get<DeliveryLocation[]>(url).pipe(
      tap((c: DeliveryLocation[]) => {
        console.log(
          `HandoverId ${handoverId} - DeliveryLocations: ${c.length} fetched`
        );
      }),
      catchError(
        this.handleError<DeliveryLocation[]>(
          `getDeliveryLocations HandoverId=${handoverId}`
        )
      )
    );
  }

  getDeliveryLocationsWell(): Observable<DeliveryLocationWell[]> {
    const url = `${this.handoversDeliverywellUrl}/delivery-ocation-well`;
    return this.http.get<DeliveryLocationWell[]>(url).pipe(
      tap((_) => console.log('fetched handover types')),
      catchError(
        this.handleError<DeliveryLocationWell[]>(`getDeliveryLocationsWell`, [])
      )
    );
  }

  getDeliveryLocationsWellsaved(
    handoverId: number | undefined
  ): Observable<DeliveryLocationWell> {
    const url = `${this.handoversDeliverywellUrl}/handovers/${handoverId}/delivery-location-well`;
    return this.http.get<DeliveryLocationWell>(url).pipe(
      tap((deliveryLoctionWell: DeliveryLocationWell) =>
        console.log('fetched handover types')
      ),
      catchError(
        this.handleError<DeliveryLocationWell>(`getDeliveryLocationsWell`)
      )
    );
  }

  saveDeliveryLocationsWell(
    handoverId: number | undefined,
    discoveryLocationWell: DeliveryLocationWell
  ): Observable<DeliveryLocationWell[]> {
    const url = `${this.handoversUrl}/${handoverId}/delivery-location-well`;
    return this.http
      .post<DeliveryLocationWell[]>(url, {
        deliveryLocationWellId: discoveryLocationWell.deliveryLocationWellId,
      })
      .pipe(
        tap((_) => console.log('fetched handover types')),
        catchError(
          this.handleError<DeliveryLocationWell[]>(
            `getDeliveryLocationsWell`,
            []
          )
        )
      );
  }

  /* Delivery Location */
  getDeliveryLocationImages(handoverId: number): Observable<Image[]> {
    const url = `${this.handoversUrl}/${handoverId}/delivery-location/images`;
    return this.http.get<Image[]>(url).pipe(
      tap((c: Image[]) => {
        console.log(
          `HandoverId ${handoverId} - getDeliveryLocationImages fetched`
        );
      }),
      catchError(
        this.handleError<Image[]>(
          `getDeliveryLocationImages HandoverId=${handoverId}`
        )
      )
    );
  }

  saveDeliveryLocations(
    deliveryLocations: DeliveryLocation[],
    handoverId: number
  ): Observable<DeliveryLocation[]> {
    const url = `${this.handoversUrl}/${handoverId}/delivery-location`;
    const deliveryLocationsToPost = deliveryLocations.map(
      (deliveryLocation: DeliveryLocation) => {
        return {
          handoverLocationStatusId: deliveryLocation.handoverLocationStatusId,
          handoverId: deliveryLocation.handoverId,
          locationStatusId: deliveryLocation.locationStatusId,
          activeSelected: deliveryLocation.activeSelected,
          taskLabel: deliveryLocation.taskLabel,
          complaintNumber: deliveryLocation.complaintNumber,
          assignedUser: deliveryLocation.assignedUser,
          agreedDate: deliveryLocation.agreedDate
            ? formatDate(
                deliveryLocation.agreedDate,
                'yyyy-MM-dd HH:mm:ss',
                this.locale
              )
            : null,
        };
      }
    );
    return this.http.post<[]>(url, deliveryLocationsToPost).pipe(
      tap((deliveryLocation: DeliveryLocation[]) => {
        console.log(`DeliveryLocations saved! ${deliveryLocation}`);
      }),
      catchError(this.handleError<DeliveryLocation[]>('saveDeliveryLocations'))
    );
  }

  deleteDeliveryLocation(
    handoverId: number,
    handoverLocationStatusId: number
  ): Observable<boolean> {
    const url = `${this.handoversUrl}/${handoverId}/delivery-location?handoverLocationStatusId=${handoverLocationStatusId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((res: boolean) => {
        console.log(`Delivery Location deleted! ${res}`);
      }),
      catchError(this.handleError<boolean>('deleteDeliveryLocation'))
    );
  }

  getRisksAlerts(handoverId: number): Observable<RisksAlertsModel[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-integrity/risks-alerts`;
    return this.http.get<RisksAlertsModel[]>(url).pipe(
      tap((r: RisksAlertsModel[]) => {
        console.log(`HandoverId ${handoverId} - getRisksAlerts fetched`);
      }),
      catchError(
        this.handleError<RisksAlertsModel[]>(
          `getRisksAlerts HandoverId=${handoverId}`
        )
      )
    );
  }

  saveRisksAlerts(
    handoverId: number,
    risksAlerts: RisksAlertsModel[]
  ): Observable<RisksAlertsModel[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-integrity/risks-alerts`;
    return this.http.post<RisksAlertsModel[]>(url, risksAlerts).pipe(
      tap((report: RisksAlertsModel[]) =>
        console.log(`RisksAlertsModel[] saved! ${report}`)
      ),
      catchError(this.handleError<RisksAlertsModel[]>(`saveRisksAlerts`))
    );
  }

  saveFileAttachment(
    handoverId: number,
    formData: FormData
  ): Observable<FileAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/file/attachment`;
    return this.http.post<FileAttachment[]>(url, formData).pipe(
      tap((uploaded: FileAttachment[]) =>
        console.log('notification attachment created')
      ),
      catchError((error) => {
        this.handleError<FileAttachment[]>('saveFileAttachment', []);
        return throwError(error);
      })
    );
  }

  deleteFileAttachment(
    handoverId: number,
    fileId: number
  ): Observable<boolean> {
    const url =
      `${this.handoversUrl}/${handoverId}/file/attachment?` +
      `fileId=${fileId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((_) => console.log('notification file deleted')),
      catchError((error) => {
        this.handleError<boolean>('deleteFileAttachment', false);
        return throwError(error);
      })
    );
  }

  getFiles(handoverId: number): Observable<FileAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/file`;
    return this.http.get<FileAttachment[]>(url).pipe(
      tap(() => console.log(`Handover ${handoverId} - File fetched`)),
      catchError(this.handleError<FileAttachment[]>(`getFiles`))
    );
  }

  updatedescriptionfile(
    handoverId: number,
    datos: string,
    fileid: number
  ): Observable<FileAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/file/attachmentUpdate`;
    const body = {
      filedescription: datos,
      fileId: fileid,
    };
    return this.http.put<FileAttachment[]>(url, body).pipe(
      tap(() => console.log('File attachment update')),
      catchError((error) => {
        this.handleError<FileAttachment[]>('saveFileAttachment', []);
        return throwError(error);
      })
    );
  }

  saveWellTreeAttachment(
    handoverId: number,
    formData: FormData
  ): Observable<WellTreeAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/tree/attachment`;
    return this.http.post<WellTreeAttachment[]>(url, formData).pipe(
      tap((uploaded: WellTreeAttachment[]) =>
        console.log('notification attachment created')
      ),
      catchError((error) => {
        this.handleError<WellTreeAttachment[]>('saveWellTreeAttachment', []);
        return throwError(error);
      })
    );
  }

  getWellTreeAttachments(handoverId: number): Observable<WellTreeAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/tree/attachment`;
    return this.http.get<WellTreeAttachment[]>(url).pipe(
      tap(() => console.log(`Handover ${handoverId} - File fetched`)),
      catchError(
        this.handleError<WellTreeAttachment[]>(`getWellTreeAttachments`)
      )
    );
  }

  deleteWellTreeAttachment(
    handoverId: number,
    fileId: number
  ): Observable<boolean> {
    const url =
      `${this.handoversUrl}/${handoverId}/tree/attachment?` +
      `fileId=${fileId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((_) => console.log('tree attachment deleted')),
      catchError((error) => {
        this.handleError<boolean>('deleteWellTreeAttachment', false);
        return throwError(error);
      })
    );
  }

  saveColumnProductionAttachment(
    handoverId: number,
    formData: FormData
  ): Observable<ProductionColumnAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/production/column/attachment`;
    return this.http.post<ProductionColumnAttachment[]>(url, formData).pipe(
      tap((uploaded: ProductionColumnAttachment[]) =>
        console.log('column production attachment created')
      ),
      catchError((error) => {
        this.handleError<ProductionColumnAttachment[]>(
          'saveColumnProductionAttachment',
          []
        );
        return throwError(error);
      })
    );
  }

  getProductionColumnAttachments(
    handoverId: number
  ): Observable<ProductionColumnAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/production/column/attachment`;
    return this.http.get<ProductionColumnAttachment[]>(url).pipe(
      tap(() => console.log(`Handover ${handoverId} - File fetched`)),
      catchError(
        this.handleError<ProductionColumnAttachment[]>(
          `getProductionColumnAttachments`
        )
      )
    );
  }

  deleteProductionColumnAttachment(
    handoverId: number,
    fileId: number
  ): Observable<boolean> {
    const url =
      `${this.handoversUrl}/${handoverId}/production/column/attachment?` +
      `fileId=${fileId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((_) => console.log('production column attachment deleted')),
      catchError((error) => {
        this.handleError<boolean>('deleteProductionColumnAttachment', false);
        return throwError(error);
      })
    );
  }

  saveWellheadAttachment(
    handoverId: number,
    formData: FormData
  ): Observable<WellheadAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/wellhead/attachment`;
    return this.http.post<WellheadAttachment[]>(url, formData).pipe(
      tap((uploaded: WellheadAttachment[]) =>
        console.log('wellhead attachment created')
      ),
      catchError((error) => {
        this.handleError<WellheadAttachment[]>('saveWellheadAttachment', []);
        return throwError(error);
      })
    );
  }

  getWellheadAttachments(handoverId: number): Observable<WellheadAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/wellhead/attachment`;
    return this.http.get<WellheadAttachment[]>(url).pipe(
      tap(() => console.log(`Handover ${handoverId} - File fetched`)),
      catchError(
        this.handleError<WellheadAttachment[]>(`getWellheadAttachments`)
      )
    );
  }

  deleteWellheadAttachment(
    handoverId: number,
    fileId: number
  ): Observable<boolean> {
    const url =
      `${this.handoversUrl}/${handoverId}/wellhead/attachment?` +
      `fileId=${fileId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((_) => console.log('wellhead attachment deleted')),
      catchError((error) => {
        this.handleError<boolean>('deleteWellheadAttachment', false);
        return throwError(error);
      })
    );
  }

  getCementingAttachmentTypes(): Observable<CementingAttachmentType[]> {
    const url = `${this.handoversUrl}/cementing/types/attachment`;
    return this.http.get<CementingAttachmentType[]>(url).pipe(
      tap((c: CementingAttachmentType[]) => {
        console.log(`getCementingAttachmentTypes fetched`);
      }),
      catchError(
        this.handleError<CementingAttachmentType[]>(
          `getCementingAttachmentTypes`
        )
      )
    );
  }

  saveCementingAttachment(
    handoverId: number,
    formData: FormData
  ): Observable<CementingFileAttachment> {
    const url = `${this.handoversUrl}/${handoverId}/cementing/attachment`;
    return this.http.post<CementingFileAttachment>(url, formData).pipe(
      tap((header: CementingFileAttachment) =>
        console.log(`cementingAttachment saved! ${header}`)
      ),
      catchError(
        this.handleError<CementingFileAttachment>(`saveCementingAttachment`)
      )
    );
  }

  getCementingAttachment(
    handoverId: number
  ): Observable<CementingFileAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/cementing/attachment`;
    return this.http.get<CementingFileAttachment[]>(url).pipe(
      tap((c: CementingFileAttachment[]) => {
        console.log(`getCementingAttachment fetched`);
      }),
      catchError(
        this.handleError<CementingFileAttachment[]>(`getCementingAttachment`)
      )
    );
  }

  deleteCementingAttachment(
    handoverId: number,
    fileId: number
  ): Observable<boolean> {
    const url =
      `${this.handoversUrl}/${handoverId}/cementing/attachment?` +
      `fileId=${fileId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((_) => console.log('cementing attachment deleted')),
      catchError((error) => {
        this.handleError<boolean>('deleteCementingAttachment', false);

        return throwError(error);
      })
    );
  }

  getExecutiveSummary(handoverId: number): Observable<ExecutiveSummary> {
    const url = `${this.handoversUrl}/${handoverId}/executive-summary`;
    return this.http.get<ExecutiveSummary>(url).pipe(
      tap((sc: ExecutiveSummary) => {
        console.log(`HandoverId ${handoverId} - executive summary fetched`),
          catchError(
            this.handleError<ExecutiveSummary>(
              `getExecutiveSummary HandoverId=${handoverId}`
            )
          );
      })
    );
  }

  saveExecutiveSummary(
    executiveSummary: ExecutiveSummary
  ): Observable<ExecutiveSummary> {
    const url = `${this.handoversUrl}/${executiveSummary.handoverId}/executive-summary`;
    return this.http.post<ExecutiveSummary>(url, executiveSummary).pipe(
      tap((executiveSummary: ExecutiveSummary) => {
        console.log(`Executive Summary saved! ${executiveSummary}`);
      }),
      catchError(this.handleError<ExecutiveSummary>('saveExecutiveSummary'))
    );
  }

  saveExecutiveSummaryAttachment(
    handoverId: number,
    formData: FormData
  ): Observable<ExecutiveSummaryAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/executive-summary/attachment`;
    return this.http.post<ExecutiveSummaryAttachment[]>(url, formData).pipe(
      tap((uploaded: ExecutiveSummaryAttachment[]) =>
        console.log('executive summary attachment created')
      ),
      catchError((error) => {
        this.handleError<ExecutiveSummaryAttachment[]>(
          'saveExecutiveSummaryAttachment',
          []
        );
        return throwError(error);
      })
    );
  }

  getExecutiveSummaryAttachments(
    handoverId: number
  ): Observable<ExecutiveSummaryAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/executive-summary/attachment`;
    return this.http.get<ExecutiveSummaryAttachment[]>(url).pipe(
      tap(() => console.log(`Handover ${handoverId} - File fetched`)),
      catchError(
        this.handleError<ExecutiveSummaryAttachment[]>(
          `getExecutiveSummaryAttachments`
        )
      )
    );
  }

  deleteExecutiveSummaryAttachment(
    handoverId: number,
    fileId: number
  ): Observable<boolean> {
    const url =
      `${this.handoversUrl}/${handoverId}/executive-summary/attachment?` +
      `fileId=${fileId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((_) => console.log('executive summary attachment deleted')),
      catchError((error) => {
        this.handleError<boolean>('deleteExecutiveSummaryAttachment', false);
        return throwError(error);
      })
    );
  }

  updateHandoverRelation(handoverId: number): Observable<boolean> {
    const url = `${this.handoversUrl}/${handoverId}/handover-relation-update`;
    const handoverToPost = {
      handoverId: handoverId,
    };
    return this.http.post<boolean>(url, handoverToPost).pipe(
      tap((_) => console.log('handover relation update')),
      catchError((error) => {
        this.handleError<boolean>('updateHandoverRelation', false);
        return throwError(error);
      })
    );
  }

  deleteHandoverRelation(handoverId: number): Observable<boolean> {
    const url = `${this.handoversUrl}/${handoverId}/handover-relation-delete`;
    const handoverToPost = {
      handoverId: handoverId,
    };
    return this.http.post<boolean>(url, handoverToPost).pipe(
      tap((_) => console.log('handover relation delted')),
      catchError((error) => {
        this.handleError<boolean>('deleteHandoverRelation', false);
        return throwError(error);
      })
    );
  }

  getHandoverRelation(handoverId: number): Observable<HandoverRelation> {
    const url = `${this.handoversUrl}/${handoverId}/handover-relation`;
    return this.http.get<HandoverRelation>(url).pipe(
      tap((handoverRelation: HandoverRelation) =>
        console.log(
          `Handover ${handoverRelation.handoverId} - relation fetched`
        )
      ),
      catchError(this.handleError<HandoverRelation>(`getHandoverRelation`))
    );
  }

  saveObservationAttachment(
    handoverId: number,
    formData: FormData
  ): Observable<ObservationAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/observation/attachment`;
    return this.http.post<ObservationAttachment[]>(url, formData).pipe(
      tap((uploaded: ObservationAttachment[]) =>
        console.log('executive summary attachment created')
      ),
      catchError((error) => {
        this.handleError<ObservationAttachment[]>(
          'saveObservationAttachment',
          []
        );
        return throwError(error);
      })
    );
  }

  getObservationAttachments(
    handoverId: number
  ): Observable<ObservationAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/observation/attachment`;
    return this.http.get<ObservationAttachment[]>(url).pipe(
      tap(() => console.log(`Handover ${handoverId} - File fetched`)),
      catchError(
        this.handleError<ObservationAttachment[]>(`getObservationAttachments`)
      )
    );
  }

  deleteObservationAttachment(
    handoverId: number,
    fileId: number
  ): Observable<boolean> {
    const url =
      `${this.handoversUrl}/${handoverId}/observation/attachment?` +
      `fileId=${fileId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((_) => console.log('observation attachment deleted')),
      catchError((error) => {
        this.handleError<boolean>('deleteObservationAttachment', false);
        return throwError(error);
      })
    );
  }

  getHandoverFracture(
    handoverId: number,
    wellId: string,
    handoverStatusId: number
  ): Observable<Fracture> {
    const url = `${this.handoversUrl}/${handoverId}/fracture?wellUwi=${wellId}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<Fracture>(url).pipe(
      tap((handoverFracture: Fracture) =>
        console.log(`Handover ${handoverId} - fracture fetched`)
      ),
      catchError(this.handleError<Fracture>(`getHandoverFracture`))
    );
  }

  getRelatedHandoverData(handoverId: number): Observable<RelatedHandover> {
    const url = `${this.handoversUrl}/${handoverId}/related-handover`;
    return this.http.get<RelatedHandover>(url).pipe(
      tap((relatedHandover: RelatedHandover) =>
        console.log(`Handover ${relatedHandover} - relation fetched`)
      ),
      catchError(this.handleError<RelatedHandover>(`getRelatedHandoverData`))
    );
  }

  getBarrierElementRelationProblems(
    handoverId: number,
    casingCount: number
  ): Observable<BarrierElementRelationProblem[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-integrity/barrier-element-relation-problem?casingCount=${casingCount}`;
    return this.http.get<BarrierElementRelationProblem[]>(url).pipe(
      tap((c: BarrierElementRelationProblem[]) => {
        console.log(
          `HandoverId ${handoverId} - getBarrierElementRelationProblems fetched`
        );
      }),
      catchError(
        this.handleError<BarrierElementRelationProblem[]>(
          `getBarrierElementRelationProblems HandoverId=${handoverId}`
        )
      )
    );
  }

  saveBarrierElementRelationProblems(
    handoverId: number,
    barrierElementRelationProblems: BarrierElementRelationProblem[]
  ): Observable<BarrierElementRelationProblem[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-integrity/barrier-element-relation-problem`;
    return this.http
      .post<BarrierElementRelationProblem[]>(
        url,
        barrierElementRelationProblems
      )
      .pipe(
        tap((report: BarrierElementRelationProblem[]) =>
          console.log(`BarrierElementRelationProblem[] saved! ${report}`)
        ),
        catchError(
          this.handleError<BarrierElementRelationProblem[]>(
            `saveBarrierElementRelationProblems`
          )
        )
      );
  }

  getProductionTypes(handoverId: number): Observable<ProductionType[]> {
    const url = `${this.handoversUrl}/${handoverId}/production-type`;
    return this.http.get<ProductionType[]>(url).pipe(
      tap((types: ProductionType[]) =>
        console.log(`Production Types ${types.length} fetched`)
      ),
      catchError(this.handleError<ProductionType[]>(`getProductionTypes`))
    );
  }

  saveProductionType(
    handoverId: number,
    productionTypeId: number
  ): Observable<boolean> {
    const url = `${this.handoversUrl}/${handoverId}/production-type?productionTypeId=${productionTypeId}`;

    return this.http.post<boolean>(url, null).pipe(
      tap((hasBeenSaved: boolean) =>
        console.log(`ProductionType ${hasBeenSaved ? '' : 'not '}saved!`)
      ),
      catchError(this.handleError<boolean>(`saveProductionType`))
    );
  }

  getAdditionalPlugs(handoverId: number): Observable<AdditionalPlug[]> {
    const url = `${this.handoversUrl}/${handoverId}/additional-plugs`;
    return this.http.get<AdditionalPlug[]>(url).pipe(
      tap((additionalPlug: AdditionalPlug[]) => {
        console.log(`HandoverId ${handoverId} fetched`),
          catchError(
            this.handleError<AdditionalPlug[]>(
              `getAdditionalPlugs HandoverId=${handoverId}`
            )
          );
      })
    );
  }

  deleteAdditionalPlugs(
    handoverId: number,
    additionalPlugId: number
  ): Observable<boolean> {
    const url = `${this.handoversUrl}/${handoverId}/additional-plugs?additionalPlugId=${additionalPlugId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((res: boolean) => {
        console.log(`handover additional plugs deleted! ${res}`);
      }),
      catchError(this.handleError<boolean>('deleteAdditionalPlugs'))
    );
  }

  saveAdditionalPlugs(
    handoverId: number,
    additionalPlugs: AdditionalPlug[]
  ): Observable<AdditionalPlug[]> {
    const url = `${this.handoversUrl}/${handoverId}/additional-plugs`;
    return this.http.post<AdditionalPlug[]>(url, additionalPlugs).pipe(
      tap((report: AdditionalPlug[]) =>
        console.log(`AdditionalPlug[] saved! ${report}`)
      ),
      catchError(this.handleError<AdditionalPlug[]>(`saveAdditionalPlugs`))
    );
  }

  saveAbandonmentReportAttachment(
    handoverId: number,
    formData: FormData
  ): Observable<ExecutiveSummaryAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/abandonment-report/attachment`;
    return this.http.post<ExecutiveSummaryAttachment[]>(url, formData).pipe(
      tap((uploaded: ExecutiveSummaryAttachment[]) =>
        console.log('abandonment report attachment created')
      ),
      catchError((error) => {
        this.handleError<ExecutiveSummaryAttachment[]>(
          'saveAbandonmentReportAttachment',
          []
        );
        return throwError(error);
      })
    );
  }

  getAbandonmentReportAttachments(
    handoverId: number
  ): Observable<ExecutiveSummaryAttachment[]> {
    const url = `${this.handoversUrl}/${handoverId}/abandonment-report/attachment`;
    return this.http.get<ExecutiveSummaryAttachment[]>(url).pipe(
      tap(() => console.log(`Handover ${handoverId} - File fetched`)),
      catchError(
        this.handleError<ExecutiveSummaryAttachment[]>(
          `getAbandonmentReportAttachments`
        )
      )
    );
  }

  deleteAbandonmentReportAttachment(
    handoverId: number,
    fileId: number
  ): Observable<boolean> {
    const url =
      `${this.handoversUrl}/${handoverId}/abandonment-report/attachment?` +
      `fileId=${fileId}`;
    return this.http.delete<boolean>(url).pipe(
      tap((_) => console.log('abandonment report attachment deleted')),
      catchError((error) => {
        this.handleError<boolean>('deleteExecutiveSummaryAttachment', false);
        return throwError(error);
      })
    );
  }

  getHandoverFractureStimulations(
    handoverId: number,
    wellName: string,
    handoverStatusId: number
  ): Observable<FractureStimulation[]> {
    const url = `${this.handoversUrl}/${handoverId}/fracture/stimulations?wellName=${wellName}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<FractureStimulation[]>(url).pipe(
      tap((fs: FractureStimulation[]) =>
        console.log(`Handover ${handoverId} - fracture Stimulations fetched`)
      ),
      catchError(
        this.handleError<FractureStimulation[]>(
          `getHandoverFractureStimulations`
        )
      )
    );
  }

  getHandoverAcidificationStimulations(
    handoverId: number,
    wellName: string,
    handoverStatusId: number
  ): Observable<AcidificationStimulation[]> {
    const url = `${this.handoversUrl}/${handoverId}/fracture/acidifications?wellName=${wellName}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<AcidificationStimulation[]>(url).pipe(
      tap((fs: AcidificationStimulation[]) =>
        console.log(
          `Handover ${handoverId} - Acidification Stimulations fetched`
        )
      ),
      catchError(
        this.handleError<AcidificationStimulation[]>(
          `getHandoverAcidificationStimulations`
        )
      )
    );
  }

  getPerforationsIcp(
    handoverId: number,
    wellUwi: string,
    handoverStatusId: number
  ): Observable<PerforationIcp[]> {
    const url = `${this.handoversUrl}/${handoverId}/perforation-icp?wellUwi=${wellUwi}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<PerforationIcp[]>(url).pipe(
      tap((fs: PerforationIcp[]) =>
        console.log(
          `Handover ${handoverId} - WellUwi ${wellUwi} - Perforations ICP fetched`
        )
      ),
      catchError(this.handleError<PerforationIcp[]>(`getPerforationsIcp`))
    );
  }

  getWellTests(
    handoverId: number,
    wellName: string,
    handoverStatusId: number
  ): Observable<WellTests[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-tests?wellName=${wellName}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<WellTests[]>(url).pipe(
      tap((wt: WellTests[]) =>
        console.log(
          `Handover ${handoverId} - WellName ${wellName} - Well Tests fetched`
        )
      ),
      catchError(this.handleError<WellTests[]>(`getWellTests`))
    );
  }

  getCementingsIcp(
    handoverId: number,
    wellName: string,
    handoverStatusId: number
  ): Observable<CementingIcp[]> {
    const url = `${this.handoversUrl}/${handoverId}/cementings-icp?wellName=${wellName}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<CementingIcp[]>(url).pipe(
      tap((wt: CementingIcp[]) =>
        console.log(
          `Handover ${handoverId} - WellName ${wellName} - Cementings ICP fetched`
        )
      ),
      catchError(this.handleError<CementingIcp[]>(`getCementingsIcp`))
    );
  }

  getBreakCasings(
    handoverId: number,
    wellName: string,
    handoverStatusId: number
  ): Observable<BreakCasing[]> {
    const url = `${this.handoversUrl}/${handoverId}/break-casings?wellName=${wellName}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<BreakCasing[]>(url).pipe(
      tap((wt: BreakCasing[]) =>
        console.log(
          `Handover ${handoverId} - WellName ${wellName} - Break Casings fetched`
        )
      ),
      catchError(this.handleError<BreakCasing[]>(`getBreakCasings`))
    );
  }

  getFishings(
    handoverId: number,
    wellName: string,
    handoverStatusId: number
  ): Observable<Fishing[]> {
    const url = `${this.handoversUrl}/${handoverId}/well-fishings?wellName=${wellName}&handoverStatusId=${handoverStatusId}`;
    return this.http.get<Fishing[]>(url).pipe(
      tap((wt: Fishing[]) =>
        console.log(
          `Handover ${handoverId} - WellName ${wellName} - Well Fishings fetched`
        )
      ),
      catchError(this.handleError<Fishing[]>(`getFishings`))
    );
  }
}
